// Was der Spieler nach einem Zug hoert und liest.
//
// Vorher stand das als Kette von `if`-Zweigen in `main.ts`, mit einem
// **Schlusszweig ohne eigene Bedingung** — und der lautete „<Booster>
// aktiviert!". Fuenf Zugarten waren einzeln behandelt, die uebrigen fielen
// durch: wer einen Level-Punkt ausgab, ein Erz aus dem Filter nahm, ein
// Wunscherz setzte oder einen Nebenauftrag abholte, las „Booster aktiviert!".
// `booster(id)` fand nichts, der Rueckfallname griff, und nichts scheiterte.
//
// **Der Fallthrough war nicht die Ursache, aber er hielt sie unsichtbar** — und
// genau das ist hier repariert: die Zuordnung ist ein `Record<MoveKind, …>`.
// Eine neue Zugart in `MoveKind` macht dieses Objekt unvollstaendig, und
// TypeScript sagt es beim naechsten `npm run typecheck`. Eine Zugart kann damit
// nicht mehr **still** in eine fremde Meldung fallen; sie kann nur noch
// vergessen werden, und das faellt auf.
//
// Es liegt hier und nicht in `main.ts`, weil `main.ts` Phaser importiert und ein
// Spiel anlegt — ohne Browser nicht ladbar. Als reine Funktion ist der Text
// pruefbar, und das hebt zugleich die Deckung, statt sie zu druecken (dieselbe
// Bewegung wie beim Hilfetext in `ui/help.ts` und den Bauteilen in `gfx/base.ts`).
//
// Gespielt wird der Klang **nicht** hier: die Funktion nennt ihn nur. So bleibt
// sie rein, und die Vollstaendigkeitspruefung deckt Meldung und Klang zugleich.

import type { BusEvents } from '../core/bus';
import { fmt } from '../core/format';
import {
  booster as boosterById,
  build as buildById,
  colonyBuildingById,
  colonyRecipeById,
  node as nodeById,
  planetById,
  projectById,
  projectsOf,
  perk as perkById,
  recipe as recipeById,
  resource as resourceById,
  sideQuest as sideQuestById,
} from '../data/balancing';
import { colonyArgs } from '../net/colony';
import type { MoveKind } from '../net/moves';
import { buildingAt, type ColonyState } from '../sim/colony';

/**
 * Genau die Nutzlast, die der Ereignisbus erwartet — **abgeleitet**, nicht
 * nachgeschrieben.
 *
 * Ein eigenes `tone: string` waere weiter gewesen als die Union des Busses und
 * haette beim Senden einen Typfehler ergeben; eine abgetippte Kopie der Union
 * waere die zweite Wahrheit, die still veraltet, sobald ein Ton dazukommt (`bad`
 * kam genau so dazu).
 */
export type MoveToast = BusEvents['toast'];

/**
 * Die Klaenge, die ein **Zug** auslöst.
 *
 * Bewusst eine Auswahl und nicht `keyof typeof sfx`: das Synthesemodul kennt
 * ein Dutzend Klaenge, und die meisten gehoeren dem Feld, nicht einem Zug. Eine
 * Ableitung waere hier weiter statt genauer — anders als bei `MoveToast`, wo
 * die abgeleitete Form die engere ist.
 */
export type MoveSound = 'upgrade' | 'quest';

export interface MoveFeedback {
  /** Die Meldung — `null`, wenn dieser Zug bewusst nichts sagt. */
  toast: MoveToast | null;
  /** Welche Klaenge dazugehoeren, in Reihenfolge. */
  sounds: readonly MoveSound[];
}

/**
 * Nur die Felder des Spielstands, die eine Meldung liest.
 *
 * Dasselbe Muster wie `TreeSave` in `sim/tree.ts`: so bleibt die Funktion rein
 * und der Test braucht keinen ganzen Spielstand. Gelesen wird der Stand **nach**
 * dem Zug — er ist die Wahrheit darueber, was gerade geschehen ist.
 */
export interface ToastSave {
  ignoredOres: Record<string, boolean>;
  wishOre: string;
  boosterStacking: boolean;
  /** Die Kolonie **nach** dem Zug — Stufe, Rezept und Schalter stehen dort. */
  colony?: ColonyState;
}

interface Zug {
  id: string;
  save: ToastSave;
  /** Wie viel der Zug erzeugt hat. Nur der Konverter benutzt es. */
  gained: number;
}

const stumm: MoveFeedback = { toast: null, sounds: [] };

const gold = (text: string, sounds: readonly MoveSound[]): MoveFeedback =>
  ({ toast: { text, tone: 'gold' }, sounds });
const gut = (text: string, sounds: readonly MoveSound[]): MoveFeedback =>
  ({ toast: { text, tone: 'good' }, sounds });
/** Eine Einstellung ist kein Erfolg — sie bekommt den neutralen Ton. */
const sachlich = (text: string): MoveFeedback =>
  ({ toast: { text, tone: '' }, sounds: ['upgrade'] });

/**
 * Die eine Zuordnung. **Vollstaendig ueber `MoveKind`** — das ist der Punkt.
 *
 * Wer eine Zugart ergaenzt und diese Tabelle vergisst, bekommt einen Typfehler.
 * Vorher bekam er eine fremde Meldung und keinen Hinweis.
 */
const FEEDBACK: Record<MoveKind, (zug: Zug) => MoveFeedback> = {
  // Gekauft wird viel; gemeldet wird nur, was einen Ast **oeffnet**. Sonst
  // stuende bei jedem Halten im Baum eine Meldung pro Stufe.
  node: ({ id }) => {
    const entry = nodeById(id);
    if (!entry?.unlock) return { toast: null, sounds: ['upgrade'] };
    return gold(`${entry.name} freigeschaltet!`, ['upgrade', 'quest']);
  },

  // Auch die Abschlussstufe ist **eine Stufe**: sie stellt den Ring hin, sie
  // springt nicht. Der Heimflug ist ein eigener, bezahlter Zug (`GAME-001`).
  build: ({ id }) => {
    const target = buildById(id);
    if (!target) return { toast: null, sounds: ['upgrade', 'quest'] };
    return gold(
      target.final
        ? `${target.name} steht — der Heimflug wartet im Ausbau-Panel`
        : `${target.name} gebaut — ${target.grants}`,
      ['upgrade', 'quest'],
    );
  },

  quest: () => gut('Belohnung abgeholt', ['quest']),

  // Vorher fiel er in die Booster-Meldung. Die Belohnung einer ganzen Kette
  // verdient ihren Namen — und den Klang des Auftrags, nicht den des Kaufs.
  side: ({ id }) =>
    gold(`${sideQuestById(id)?.name ?? 'Nebenauftrag'} abgeschlossen — Belohnung abgeholt`, ['quest']),

  booster: ({ id }) => gut(`${boosterById(id)?.name ?? 'Booster'} aktiviert!`, ['upgrade']),

  perk: ({ id }) => gut(`${perkById(id)?.name ?? 'Bonus'} verbessert`, ['upgrade']),

  // Die **Menge** statt der Zahl der Durchlaeufe: „12 Verbundsubstanz" sagt, was
  // im Lager liegt, „3× Grundverbund" nur, wie oft die Presse lief.
  convert: ({ id, gained }) => {
    const ausgang = recipeById(id)?.outRes;
    const name = ausgang ? resourceById(ausgang)?.name : undefined;
    return gut(`${fmt(gained)} ${name ?? 'Verbund'} hergestellt`, ['upgrade']);
  },

  // Die Richtung gehoert in den Text: „ein" und „aus" sind zwei Ereignisse, und
  // beim Klicken ist der Chip nicht im Blick.
  ore: ({ id, save }) => {
    const name = resourceById(id)?.name ?? 'Das Erz';
    return sachlich(
      save.ignoredOres[id] === true
        ? `${name} wird nicht mehr abgebaut`
        : `${name} wird wieder abgebaut`,
    );
  },

  // Gelesen wird der Spielstand, nicht die Zug-ID: ein Abwaehlen setzt das
  // Wunscherz auf leer, und dann meldete die ID das eben abgewaehlte Erz.
  // „Keins" ist ein gueltiger Zustand und bekommt darum eine eigene Meldung —
  // ein Name aus dem Nichts waere ein Text mit einer Luecke darin.
  wish: ({ save }) => {
    const gewuenscht = save.wishOre;
    if (!gewuenscht) return sachlich('Kein Wunscherz — die Drohnen graben, was am nächsten liegt');
    const name = resourceById(gewuenscht)?.name;
    return sachlich(`Wunscherz ${name ?? gewuenscht} — die Drohnen holen es bevorzugt`);
  },

  // Der Schalter meldet seinen **neuen Zustand**, nicht „umgeschaltet": beim
  // Klicken ist der Knopf nicht im Blick, und „umgeschaltet" beantwortet die
  // Frage nicht, die man danach hat. Gelesen wird — wie ueberall hier — der
  // Stand **nach** dem Zug.
  // Und er sagt **nicht** „Booster": ein Gate nebenan hält fest, dass keine
  // fremde Zugart die Booster-Meldung nachspricht — es kann eine berechtigte
  // Erwähnung nicht von einem Fallthrough unterscheiden, und das ist die
  // richtige Seite, auf der ein billiges Gate irren darf.
  boostermode: ({ save }) => sachlich(
    save.boosterStacking
      ? 'Zünden stapelt jetzt — stärker statt länger'
      : 'Zünden verlängert jetzt — länger statt stärker',
  ),

  // Die Sequenz spricht selbst, und sie zeigt nur, was schon gilt (`VIEW-001`).
  home: () => stumm,

  // ── Akt III: die Kolonie ──────────────────────────────────────────────────
  // Gelesen wird auch hier der Stand **nach** dem Zug: die Stufe, auf der eine
  // Halle jetzt steht, nicht die, von der sie kam — ein Mehrfachausbau kann
  // eine, zehn oder hundert Stufen gebracht haben.
  land: ({ id }) => gold(`Gelandet auf ${planetById(id)?.name ?? 'dem Planeten'}`, ['quest']),

  place: ({ id }) => {
    const art = colonyBuildingById(colonyArgs('place', id)?.kind ?? '');
    return gut(`${art?.name ?? 'Anlage'} errichtet`, ['upgrade']);
  },

  upgrade: ({ id, save }) => {
    const anlage = colonyAnlage('upgrade', id, save);
    const name = colonyBuildingById(anlage?.kind ?? '')?.name ?? 'Anlage';
    return anlage ? gut(`${name} auf Stufe ${anlage.level}`, ['upgrade']) : stumm;
  },

  recipe: ({ id, save }) => {
    const anlage = colonyAnlage('recipe', id, save);
    const name = colonyBuildingById(anlage?.kind ?? '')?.name ?? 'Die Anlage';
    const band = colonyRecipeById(anlage?.recipe ?? '')?.name;
    return band ? sachlich(`${name}: ${band}`) : stumm;
  },

  // Die Richtung gehört in den Text, wie beim Erzfilter.
  switch: ({ id, save }) => {
    const anlage = colonyAnlage('switch', id, save);
    const name = colonyBuildingById(anlage?.kind ?? '')?.name ?? 'Die Anlage';
    if (!anlage) return stumm;
    return sachlich(anlage.on ? `${name} läuft wieder` : `${name} angehalten — sie verbraucht nichts`);
  },

  relocate: ({ id }) => {
    const ziel = colonyArgs('relocate', id);
    return ziel ? sachlich('Anlage versetzt') : stumm;
  },

  // Das **abgeschlossene** Projekt ist das letzte vor dem Zähler — gelesen nach
  // dem Zug, wie überall hier.
  project: ({ id, save }) => {
    const fertig = save.colony?.planets[id]?.projects ?? 0;
    const projekt = projectsOf(id)[fertig - 1] ?? projectById(id);
    return gold(`${projekt?.name ?? 'Projekt'} abgeschlossen`, ['upgrade', 'quest']);
  },
};

function colonyAnlage(kind: 'upgrade' | 'recipe' | 'switch', id: string, save: ToastSave) {
  const zug = colonyArgs(kind, id);
  const outpost = zug ? save.colony?.planets[zug.planet] : undefined;
  return zug && outpost ? buildingAt(outpost, zug.x ?? -1, zug.y ?? -1) : undefined;
}

/** Meldung und Klang eines Zuges. Rein — sie spielt nichts und schreibt nichts. */
export function moveFeedback(
  kind: MoveKind,
  id: string,
  save: ToastSave,
  gained = 0,
): MoveFeedback {
  return FEEDBACK[kind]({ id, save, gained });
}
